// Gestion centralisée de tous les documents légaux de la boutique
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileCheck, Scale, Truck, Package, Receipt, RotateCcw, Shield,
  Save, Loader2, Upload, X, Check, Lock, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../../config/api';

// ─── Configuration des 7 documents ─────────────────────────────────────────
const DOCUMENTS = [
  {
    id: 'cgv',
    title: 'CGV',
    fullTitle: 'Conditions Générales de Vente',
    icon: FileCheck,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    productOnly: false,
    description: 'Règles encadrant vos ventes : obligations du vendeur et de l\'acheteur, modalités de commande et paiement.',
    placeholder: `CONDITIONS GÉNÉRALES DE VENTE

Article 1 – Objet
Les présentes conditions générales de vente s'appliquent à toutes les commandes passées sur notre boutique.

Article 2 – Prix
Les prix sont indiqués en euros, toutes taxes comprises (TTC).

Article 3 – Paiement
Le règlement s'effectue à la commande par carte bancaire ou virement.

Article 4 – Délai de réflexion
Conformément à l'article L221-18 du Code de la consommation, vous disposez d'un délai de 14 jours pour exercer votre droit de rétractation.`,
  },
  {
    id: 'mentions_legales',
    title: 'Mentions légales',
    fullTitle: 'Mentions Légales',
    icon: Scale,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    productOnly: false,
    description: 'Informations obligatoires sur votre identité, vos coordonnées et l\'hébergeur du site.',
    placeholder: `MENTIONS LÉGALES

Éditeur du site
Raison sociale : [Votre société]
Forme juridique : [SARL / SAS / Auto-entrepreneur…]
SIRET :
Adresse :
Téléphone :
Email :

Directeur de la publication
[Prénom Nom]

Hébergement
Le présent site est hébergé par :
[Nom hébergeur], [Adresse hébergeur]`,
  },
  {
    id: 'frais_livraison',
    title: 'Frais de livraison',
    fullTitle: 'Frais de Livraison',
    icon: Truck,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    productOnly: true,
    description: 'Tarifs, zones géographiques et délais de livraison applicables à vos produits.',
    placeholder: `FRAIS DE LIVRAISON

France métropolitaine
• Colissimo (2-3 jours ouvrés) : 5,90 €
• Chronopost (24h) : 12,90 €
• Livraison offerte dès 80 € d'achat

Europe
• À partir de 12,90 € selon destination

Autres destinations
• Nous contacter pour un devis personnalisé

Les délais indiqués courent à compter de la validation du paiement.`,
  },
  {
    id: 'modalites_expedition',
    title: "Modalités d'expédition",
    fullTitle: "Modalités d'Expédition",
    icon: Package,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    productOnly: true,
    description: 'Transporteurs partenaires, conditionnement, délais de préparation des commandes.',
    placeholder: `MODALITÉS D'EXPÉDITION

Préparation des commandes
Les commandes sont préparées sous 24 à 48 heures ouvrées après validation du paiement.

Transporteurs partenaires
Nous travaillons avec : La Poste (Colissimo), Chronopost, DHL.

Emballage
Vos produits sont soigneusement emballés pour garantir leur intégrité pendant le transport.

Suivi de commande
Un email avec numéro de suivi vous est envoyé dès l'expédition.`,
  },
  {
    id: 'taxes',
    title: 'Taxes',
    fullTitle: 'Informations sur les Taxes',
    icon: Receipt,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    productOnly: true,
    description: 'TVA applicable, taux pratiqués et exonérations éventuelles.',
    placeholder: `INFORMATIONS SUR LES TAXES

Assujettissement à la TVA
Notre entreprise est assujettie à la TVA. Numéro de TVA intracommunautaire : FR XX XXX XXX XXX

Taux de TVA appliqués
• Taux normal (20 %) : produits standard
• Taux intermédiaire (10 %) : [si applicable]
• Taux réduit (5,5 %) : [si applicable]

Clients professionnels (B2B)
Pour les clients professionnels intracommunautaires disposant d'un numéro de TVA valide, la TVA peut être exonérée sur présentation de ce numéro.`,
  },
  {
    id: 'confidentialite',
    title: 'Confidentialité',
    fullTitle: 'Sécurité & Confidentialité',
    icon: Shield,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    productOnly: false,
    description: 'Politique de protection des données personnelles (RGPD) et sécurité des transactions.',
    placeholder: `POLITIQUE DE CONFIDENTIALITÉ

Collecte des données personnelles
Nous collectons uniquement les données nécessaires au traitement de vos commandes et à la gestion de votre compte client : nom, prénom, adresse email, adresse postale, téléphone.

Finalité du traitement
Ces données sont utilisées exclusivement pour :
• Traiter et livrer vos commandes
• Vous envoyer des confirmations par email
• Améliorer notre service

Conservation des données
Vos données sont conservées 3 ans après votre dernière commande, conformément à la réglementation RGPD.

Vos droits
Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données. Pour exercer ces droits : [email de contact]

Sécurité
Les paiements sont sécurisés par chiffrement SSL. Nous ne stockons aucune donnée bancaire.`,
  },
  {
    id: 'retours',
    title: 'Retours',
    fullTitle: 'Retours & Remboursements',
    icon: RotateCcw,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    productOnly: true,
    description: 'Conditions de retour produits, délais légaux et procédure de remboursement.',
    placeholder: `RETOURS ET REMBOURSEMENTS

Droit de rétractation
Conformément à l'article L221-18 du Code de la consommation, vous disposez de 14 jours à compter de la réception pour exercer votre droit de rétractation, sans justification.

Procédure de retour
1. Contactez-nous à [email] en indiquant votre numéro de commande
2. Nous vous envoyons une étiquette de retour prépayée
3. Renvoyez le produit dans son emballage d'origine, non utilisé
4. Le remboursement est effectué sous 14 jours après réception du retour

Frais de retour
Les frais de retour sont à notre charge dans le cadre du droit de rétractation légal.

Produits défectueux
En cas de produit défectueux, contactez-nous immédiatement. Le remplacement ou remboursement intégral est garanti.`,
  },
];

const getAuthToken = () =>
  localStorage.getItem('social_token') ||
  localStorage.getItem('auth_token') ||
  localStorage.getItem('client_token');

// ─── Composant principal ────────────────────────────────────────────────────
export default function DocumentsLegauxPage() {
  const [activeDoc, setActiveDoc] = useState('cgv');
  const [vendeurType, setVendeurType] = useState('artisan'); // artisan | produits | mixte
  const [documents, setDocuments] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const fileInputRef = useRef(null);

  const activeDocConfig = DOCUMENTS.find(d => d.id === activeDoc);
  const isLocked = activeDocConfig?.productOnly && vendeurType === 'artisan';
  const filledCount = DOCUMENTS.filter(d => documents[d.id]?.trim() || uploadedFiles[d.id]).length;

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const token = getAuthToken();
      if (!token) { setLoading(false); return; }
      const res = await fetch(`${API_URL}/api/cockpit/boutique/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || {});
        setUploadedFiles(data.files || {});
        setVendeurType(data.vendeur_type || 'artisan');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getAuthToken();
      if (!token) { toast.error('Non connecté'); return; }
      const res = await fetch(`${API_URL}/api/cockpit/boutique/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ documents, vendeur_type: vendeurType }),
      });
      if (res.ok) {
        toast.success('Documents sauvegardés !');
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2500);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Seuls les fichiers PDF sont acceptés'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
    setUploadedFiles(prev => ({ ...prev, [activeDoc]: file.name }));
    toast.success(`${file.name} sélectionné`);
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Documents Légaux</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="font-medium text-[#1E3A5F]">{filledCount}</span>
            <span className="text-gray-400">/{DOCUMENTS.length} documents renseignés</span>
          </p>
        </div>

        {/* Type de vendeur */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 hidden sm:block">Mon activité :</span>
          <div className="flex rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
            {[
              { value: 'artisan', label: 'Services' },
              { value: 'mixte', label: 'Les deux' },
              { value: 'produits', label: 'Produits' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setVendeurType(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium transition-all ${
                  vendeurType === opt.value
                    ? 'bg-[#1E3A5F] text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Sidebar gauche */}
        <nav className="w-60 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0 py-3">
          <p className="px-4 mb-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            Documents
          </p>
          <div className="space-y-0.5 px-2">
            {DOCUMENTS.map(doc => {
              const Icon = doc.icon;
              const locked = doc.productOnly && vendeurType === 'artisan';
              const filled = !!(documents[doc.id]?.trim() || uploadedFiles[doc.id]);
              const active = activeDoc === doc.id;

              return (
                <button
                  key={doc.id}
                  onClick={() => setActiveDoc(doc.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                    active ? `${doc.bg} border ${doc.border}` : 'hover:bg-gray-50 border border-transparent'
                  } ${locked ? 'opacity-40' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? doc.bg : 'bg-gray-100'}`}>
                    {locked
                      ? <Lock size={13} className="text-gray-400" />
                      : <Icon size={13} className={active ? doc.color : 'text-gray-500'} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${active ? doc.color : 'text-gray-700'}`}>
                      {doc.title}
                    </p>
                    {doc.productOnly && (
                      <p className="text-[10px] text-orange-400 font-medium leading-tight">Produits</p>
                    )}
                  </div>
                  {filled && !locked && (
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Légende */}
          <div className="mt-6 mx-3 p-3 bg-gray-50 rounded-xl">
            <p className="text-[10px] text-gray-400 font-medium mb-1.5">Légende</p>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-[10px] text-gray-500">Document renseigné</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-orange-400 font-medium">Produits</span>
              <span className="text-[10px] text-gray-500">= vendeurs de produits</span>
            </div>
          </div>
        </nav>

        {/* Zone d'édition */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeDocConfig && (
            <>
              {/* En-tête du document actif */}
              <div className={`px-6 py-4 border-b ${activeDocConfig.bg} flex-shrink-0`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${activeDocConfig.bg} border ${activeDocConfig.border} flex items-center justify-center flex-shrink-0`}>
                      <activeDocConfig.icon size={18} className={activeDocConfig.color} />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">{activeDocConfig.fullTitle}</h2>
                      <p className="text-sm text-gray-500 leading-tight mt-0.5">{activeDocConfig.description}</p>
                    </div>
                  </div>
                  {activeDocConfig.productOnly && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 rounded-full text-xs font-medium text-orange-600 flex-shrink-0">
                      <Package size={11} />
                      Vendeurs de produits
                    </span>
                  )}
                </div>
              </div>

              {/* Contenu: verrouillé ou éditeur */}
              {isLocked ? (
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center max-w-xs">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Lock size={28} className="text-gray-400" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-700 mb-2">
                      Section réservée aux vendeurs de produits
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Activez la vente de produits pour accéder à cette section.
                    </p>
                    <button
                      onClick={() => setVendeurType('mixte')}
                      className="px-4 py-2 bg-[#1E3A5F] text-white text-sm font-semibold rounded-xl hover:bg-[#2D4A6F] transition-colors"
                    >
                      Activer la vente de produits
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-6 space-y-5">

                  {/* Upload PDF */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Upload size={14} className="text-gray-400" />
                      Option 1 — Charger un PDF existant
                    </p>
                    {uploadedFiles[activeDoc] ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                        <FileText size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-700 font-medium flex-1 truncate">
                          {uploadedFiles[activeDoc]}
                        </span>
                        <Check size={15} className="text-green-500 flex-shrink-0" />
                        <button
                          onClick={() => setUploadedFiles(prev => { const n = { ...prev }; delete n[activeDoc]; return n; })}
                          className="p-1 hover:bg-green-100 rounded-lg transition-colors"
                        >
                          <X size={14} className="text-green-600" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full flex items-center gap-3 p-3.5 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#1E3A5F]/30 hover:bg-gray-50 transition-all text-left group"
                      >
                        <div className="w-10 h-10 bg-gray-100 group-hover:bg-[#1E3A5F]/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                          <Upload size={16} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600 group-hover:text-gray-800">Télécharger un PDF</p>
                          <p className="text-xs text-gray-400">Format PDF uniquement • Max 5 Mo</p>
                        </div>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
                  </div>

                  {/* Séparateur */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium bg-gray-50 px-3 py-1 rounded-full border border-gray-200">ou</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Éditeur texte */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FileText size={14} className="text-gray-400" />
                      Option 2 — Rédiger le contenu
                    </p>
                    <textarea
                      value={documents[activeDoc] || ''}
                      onChange={e => setDocuments(prev => ({ ...prev, [activeDoc]: e.target.value }))}
                      placeholder={activeDocConfig.placeholder}
                      rows={18}
                      className="w-full p-4 text-sm text-gray-700 border border-gray-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]/40 font-mono leading-relaxed bg-white placeholder-gray-300"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {(documents[activeDoc] || '').length} caractères
                    </p>
                  </div>

                  {/* Alerte si ni PDF ni texte */}
                  {!uploadedFiles[activeDoc] && !documents[activeDoc]?.trim() && (
                    <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <AlertCircle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        Ce document n'est pas encore renseigné. Uploadez un PDF ou rédigez le contenu ci-dessus.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Pied de page — bouton sauvegarder */}
              {!isLocked && (
                <div className="border-t border-gray-200 px-6 py-3 bg-white flex items-center justify-between flex-shrink-0">
                  <p className="text-xs text-gray-400">
                    Tous les documents sont sauvegardés ensemble
                  </p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl transition-all disabled:opacity-50 ${
                      justSaved
                        ? 'bg-green-500 text-white'
                        : 'bg-[#1E3A5F] text-white hover:bg-[#2D4A6F]'
                    }`}
                  >
                    {saving ? (
                      <><Loader2 size={15} className="animate-spin" /> Sauvegarde…</>
                    ) : justSaved ? (
                      <><Check size={15} /> Sauvegardé !</>
                    ) : (
                      <><Save size={15} /> Sauvegarder</>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
